import React, {useMemo} from 'react'
import ComponentButton from '../ComponentButton'
import {useField, useFormikContext} from 'formik'
import {Component} from '../../modules/ModuleForm'
import {Col, Form} from 'react-bootstrap'
import {KTSVG} from '../../../../_metronic/helpers'
import {useIntl} from 'react-intl'
import ComponentTooltip from '../ComponentTooltip'
import {
    TComponentSmartList,
    TComponentSmartListPropertiesRow,
    TComponentSmartListProperty,
} from './_types'
import {useModalContext} from '../../modules/helpers/ModalContext'
import {TComponentButton} from '../ComponentButton/_types'

type TAnswer = {
    text: string
    isCorrect: boolean | null
    isUserAnswer: string
}

const ComponentSmartListProperty = React.memo<TComponentSmartListProperty>((props) => {
    const {
        title,
        is_required,
        size,
        is_headers_shown,
        index,
        is_visible,
        is_disabled,
        isListBusy,
        headerButtons = [],
        onHeaderButtonClick,
    } = props
    const showTitle = title && (is_headers_shown ? true : index === 0 ? true : false)
    const resolvedSize = size ? size * 3 : 3
    const disabled = Boolean(is_disabled || isListBusy)

    if (is_visible === false) {
        return null
    }
    return (
        <Form.Group className='componentSmartList_property' as={Col} md={resolvedSize}>
            {showTitle ? (
                <Form.Group as={Col} md={12}>
                    <ComponentTooltip title={title}>
                        <label
                            className={`componentSmartList_propertyLabel${
                                is_required ? ' required' : ''
                            }`}
                        >
                            {title}
                        </label>
                    </ComponentTooltip>
                </Form.Group>
            ) : null}
            <Form.Group as={Col} md={12}>
                <div className='componentSmartList_propertyField d-flex align-items-center gap-2'>
                    <div className='componentSmartList_fieldControl flex-grow-1'>
                        <Component {...props} is_disabled={disabled} />
                    </div>
                    {headerButtons.length > 0 && (
                        <div className='componentSmartList_fieldAddon'>
                            {headerButtons.map((btn) => {
                                if (btn.type === 'modal') {
                                    const enriched: TComponentButton = {
                                        ...btn,
                                        settings: {
                                            ...btn.settings,
                                            insert_to_field: props.article,
                                        },
                                    }

                                    return (
                                        <ComponentButton
                                            key={btn.type + btn.settings.title}
                                            {...enriched}
                                            defaultLabel='icon'
                                            className='smartList_fieldAddonButton'
                                            disabled={disabled}
                                        />
                                    )
                                }

                                return (
                                    <ComponentButton
                                        key={btn.type + (btn as any).settings?.title}
                                        {...btn}
                                        defaultLabel='icon'
                                        className='smartList_fieldAddonButton'
                                        disabled={disabled}
                                    />
                                )
                            })}
                        </div>
                    )}
                </div>
            </Form.Group>
        </Form.Group>
    )
})

const ComponentSmartListPropertiesRow: React.FC<TComponentSmartListPropertiesRow> = (props) => {
    const {
        parentArticle,
        properties,
        is_headers_shown,
        parentIndex,
        hook,
        handleDeleteRow,
        isAllDisabled,
        onCellButtonClick,
    } = props
    return (
        <div className='componentSmartList_propertiesRow'>
            {!isAllDisabled && (
                <button
                    type='button'
                    className='componentSmartList_propertiesRowButton'
                    onClick={handleDeleteRow}
                >
                    <KTSVG path='/media/crm/icons/close.svg' />
                </button>
            )}
            {properties.map((field) => (
                <ComponentSmartListProperty
                    key={field.article}
                    index={parentIndex}
                    {...field}
                    article={`${parentArticle}.${field.article}`}
                    hook={hook}
                    is_headers_shown={is_headers_shown}
                    isListBusy={isAllDisabled}
                    onHeaderButtonClick={(btn) =>
                        onCellButtonClick
                            ? onCellButtonClick(btn, `${parentArticle}.${field.article}`)
                            : undefined
                    }
                />
            ))}
        </div>
    )
}

const ComponentSmartListTestRow: React.FC<TComponentSmartListPropertiesRow> = (props) => {
    const {parentArticle, properties, parentIndex, hook, handleDeleteRow, isAllDisabled} = props
    const {values} = useFormikContext<any>()
    const currentRow = parentArticle.split('.').reduce((acc, key) => {
        if (key.endsWith(']')) {
            const [arrKey, index] = key.match(/([^\[]+)\[(\d+)\]/)!.slice(1, 3)
            return acc?.[arrKey]?.[+index]
        }
        return acc?.[key]
    }, values)

    const isUserAnswer = currentRow?.isUserAnswer === true
    const isWrongAnswer = currentRow?.isUserAnswer === true && currentRow?.isCorrect === false

    return (
        <div
            className={`componentSmartListTest_row 
    ${isUserAnswer ? 'smartList-row-correct' : ''} 
    ${isWrongAnswer ? 'smartList-row-wrong' : ''}`}
        >
            <div className='componentSmartListTest_rowHeader'>
                <div className='componentSmartListTest_index'>#{parentIndex + 1}</div>
                <div className='componentSmartListTest_body'>
                    {properties
                        .filter((field) => field.is_visible !== false)
                        .map((field) => {
                            const article = `${parentArticle}.${field.article}`

                            const isMain =
                                field.field_type === 'string' &&
                                (field.article === 'text' || /text/i.test(field.article))
                            const isSmall =
                                field.field_type === 'integer' ||
                                field.field_type === 'checkbox' ||
                                /score|point|isCorrect/i.test(field.article)

                            return (
                                <div
                                    key={field.article}
                                    className={
                                        isMain
                                            ? 'componentSmartListTest_main'
                                            : isSmall
                                            ? 'componentSmartListTest_side'
                                            : 'componentSmartListTest_side'
                                    }
                                >
                                    {field.title && (
                                        <label
                                            style={{
                                                fontWeight: 'bold',
                                                fontSize: '0.95rem',
                                                marginBottom: '4px',
                                                display: 'block',
                                            }}
                                        >
                                            {field.title}
                                        </label>
                                    )}
                                    <Component {...field} article={article} hook={hook} />
                                </div>
                            )
                        })}
                </div>
                {!isAllDisabled && (
                    <button
                        type='button'
                        className='componentSmartListTest_remove'
                        onClick={handleDeleteRow}
                    >
                        <KTSVG path='/media/crm/icons/close.svg' />
                    </button>
                )}
            </div>
        </div>
    )
}

const ComponentSmartList: React.FC<TComponentSmartList> = (props) => {
    const {article, properties, hook, is_headers_shown, isTest, is_disabled, buttons = []} = props
    const intl = useIntl()
    const [field] = useField(article)
    const {value} = field
    const {values, setFieldValue} = useFormikContext<any>()
    const [{value: fieldsDisabled}] = useField('fieldsDisabled')
    const isBusy = Boolean(is_disabled || fieldsDisabled?.[article])
    const modal = useModalContext()

    const mapRowFromModal = (raw: any) => {
        const r = typeof raw === 'object' && raw?.data ? raw.data : raw
        return {
            id: r.id ?? r.value ?? r.row_id ?? null,
            title: r.title ?? r.name ?? `#${r.id ?? ''}`,
            ...r,
        }
    }

    const handleCellButtonClick = (btn: TComponentButton, cellArticle: string) => {
        const s: any = btn.settings || {}
        if (btn.type === 'modal') {
            modal.setShow(true, {
                title: s.title || 'Добавить',
                page: s.page,
                object: s.object,
                command: s.command || 'add',
                close_after_submit: true,
                handleResponse: (response: any) => {
                    const r = mapRowFromModal(response?.data ?? response)
                    const newId = r.id ?? r.value
                    setFieldValue(cellArticle, newId)
                },
            })
            return
        }
        if (btn.type === 'custom' && typeof (btn as any).customHandler === 'function') {
            ;(btn as any).customHandler()
        }
    }

    const handleHeaderButtonClick = (btn: TComponentButton) => {
        const s: any = btn.settings || {}
        const targetField: string = s.insert_to_field || article
        if (btn.type === 'modal') {
            modal.setShow(true, {
                title: s.title || 'Добавить',
                page: s.page,
                object: s.object,
                command: s.command || 'add',
                close_after_submit: true,
                handleResponse: (response: any) => {
                    const prev = Array.isArray(values?.[targetField]) ? values[targetField] : []
                    const row = mapRowFromModal(response?.data ?? response)
                    setFieldValue(targetField, [...prev, row])
                },
            })
        }
    }

    const propertiesWithButtons = useMemo(() => {
        return properties.map((p) => ({
            ...p,
            headerButtons: buttons.filter(
                (b: any) => b?.settings?.field_place === `${article}.${p.article}`
            ),
        }))
    }, [properties, buttons, article])

    const handleAppendRow = () => {
        const valuesClone = Array.isArray(value) ? [...value] : []
        const newPropertyObject: any = {}
        properties.forEach((property) => (newPropertyObject[property.article] = undefined))
        valuesClone.push(newPropertyObject)
        setFieldValue(article, valuesClone)
    }
    const handleDeleteRow = (index: number) => {
        const valuesClone = Array.isArray(value) ? [...value] : []
        valuesClone.splice(index, 1)
        setFieldValue(article, valuesClone)
    }

    if (isTest) {
        const isAllDisabled = isBusy || properties.every((p) => p.is_disabled === true)
        return (
            <div className='componentSmartListTest'>
                <div className='componentSmartListTest_list'>
                    {value?.map?.((_: any, index: number) => {
                        return (
                            <ComponentSmartListTestRow
                                key={`${article}[${index}]`}
                                parentArticle={`${article}[${index}]`}
                                parentIndex={index}
                                properties={properties}
                                hook={hook}
                                handleDeleteRow={() => handleDeleteRow(index)}
                                isAllDisabled={isAllDisabled}
                                onCellButtonClick={handleCellButtonClick}
                            />
                        )
                    })}
                </div>

                {!isAllDisabled && (
                    <ComponentButton
                        className='componentSmartListTest_addButton'
                        type='custom'
                        settings={{
                            title: intl.formatMessage({id: 'BUTTON.APPEND'}),
                            icon: '',
                            background: 'dark',
                        }}
                        customHandler={handleAppendRow}
                    />
                )}
            </div>
        )
    }

    return (
        <div className='componentSmartList'>
            <div className='componentSmartList_propertiesContainer'>
                {value?.map?.((value: any, index: number) => {
                    return (
                        <ComponentSmartListPropertiesRow
                            key={`${article}[${index}]`}
                            parentArticle={`${article}[${index}]`}
                            parentIndex={index}
                            properties={propertiesWithButtons}
                            is_headers_shown={is_headers_shown}
                            hook={hook}
                            handleDeleteRow={() => handleDeleteRow(index)}
                            isAllDisabled={isBusy}
                        />
                    )
                })}
            </div>
            <ComponentButton
                className='componentSmartList_addButton'
                type='custom'
                settings={{
                    title: intl.formatMessage({id: 'BUTTON.APPEND'}),
                    icon: '',
                    background: 'dark',
                }}
                customHandler={handleAppendRow}
                disabled={isBusy}
            />
        </div>
    )
}
export default ComponentSmartList
